import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, Globe, Sparkles} from "lucide-react";
import gsap from "gsap";

export default function CalendlyPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
 

  const heroRef = useRef(null);
  const titleRef = useRef(null);
  const subRef = useRef(null);
  const perksRef = useRef(null);
  const iframeContainerRef = useRef(null);
  const blob1Ref = useRef(null);
  const blob2Ref = useRef(null);
  const blob3Ref = useRef(null);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    document.body.appendChild(script);

    const handleEvent = (e) => {
      if (e.data?.event === "calendly.event_scheduled") {
        setTimeout(() => navigate("/"), 3000);
      }
    };
    window.addEventListener("message", handleEvent);

    // GSAP entrance
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power4.out" } });
      tl.to(titleRef.current, { y: 0, opacity: 1, duration: 1.2, delay: 0.3 })
        .to(subRef.current, { y: 0, opacity: 1, duration: 1 }, "-=0.7")
        .to(perksRef.current, { y: 0, opacity: 1, duration: 0.8 }, "-=0.6")
        .to(iframeContainerRef.current, { y: 0, opacity: 1, scale: 1, duration: 1 }, "-=0.5");
    });

    return () => {
      window.removeEventListener("message", handleEvent);
      if (document.body.contains(script)) document.body.removeChild(script);
      ctx.revert();
    };
  }, [navigate]);

  // RAF-throttled mouse parallax
  useEffect(() => {
    let ticking = false;
    const handleMouseMove = (e) => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const x = (e.clientX / window.innerWidth - 0.5) * 2;
          const y = (e.clientY / window.innerHeight - 0.5) * 2;
          if (blob1Ref.current) blob1Ref.current.style.transform = `translate3d(${x * 40}px, ${y * 40}px, 0)`;
          if (blob2Ref.current) blob2Ref.current.style.transform = `translate3d(${x * -50}px, ${y * 25}px, 0)`;
          if (blob3Ref.current) blob3Ref.current.style.transform = `translate3d(${x * 25}px, ${y * -40}px, 0)`;
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const perks = [
    { icon: <Clock className="w-5 h-5" />, label: "30 min", desc: "Focused session" },
    { icon: <Globe className="w-5 h-5" />, label: "Worldwide", desc: "Your timezone" },
    { icon: <Calendar className="w-5 h-5" />, label: "Flexible", desc: "Reschedule anytime" },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-hidden relative selection:bg-blue-500/30">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=Playfair+Display:ital,wght@0,400;0,600;1,400;1,600&display=swap');
        .font-playfair { font-family: 'Playfair Display', serif; }
        .font-inter { font-family: 'Inter', sans-serif; }
        .text-gradient-mask {
          background: linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.6) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .noise-overlay {
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.02'/%3E%3C/svg%3E");
        }
        .calendly-glow {
          box-shadow: 0 0 80px rgba(59,130,246,0.15), 0 0 160px rgba(147,51,234,0.08);
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .shimmer-text {
          background: linear-gradient(90deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,1) 50%, rgba(255,255,255,0.4) 100%);
          background-size: 200% 100%;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: shimmer 3s ease-in-out infinite;
        }
      `}</style>

      {/* Noise */}
      <div className="fixed inset-0 noise-overlay pointer-events-none z-[1] opacity-30" />

      {/* Parallax blobs */}
      <div ref={blob1Ref} className="fixed -top-40 -left-40 w-[700px] h-[700px] rounded-full bg-blue-600/12 blur-[140px] opacity-70 pointer-events-none z-0 will-change-transform transition-transform duration-100 ease-out" />
      <div ref={blob2Ref} className="fixed bottom-[-150px] right-[-150px] w-[600px] h-[600px] rounded-full bg-purple-600/12 blur-[120px] opacity-60 pointer-events-none z-0 will-change-transform transition-transform duration-100 ease-out" />
      <div ref={blob3Ref} className="fixed top-1/3 left-1/2 -translate-x-1/2 w-[800px] h-[500px] rounded-full bg-indigo-600/8 blur-[160px] opacity-50 pointer-events-none z-0 will-change-transform transition-transform duration-100 ease-out" />

      {/* Back button — floating, minimal */}
      <button
        onClick={() => navigate("/")}
        className="fixed top-6 left-6 z-50 group flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/[0.03] border border-white/10 backdrop-blur-xl hover:bg-white/[0.08] hover:border-white/20 transition-all duration-300 text-sm font-medium text-gray-400 hover:text-white"
      >
        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
        <span className="hidden sm:inline">Back</span>
      </button>

      <div className="relative z-10">
        {/* Hero — tight, commanding */}
        <section ref={heroRef} className="pt-24 pb-8 px-4 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-xs font-medium text-blue-400 mb-6">
            <Sparkles className="w-3 h-3" />
            Limited availability this month
          </div>

          <h1
            ref={titleRef}
            className="font-inter text-4xl sm:text-5xl md:text-7xl font-black tracking-tight text-gradient-mask opacity-0 translate-y-8"
          >
            Let's Build Something
            <span className="font-playfair italic font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500 block mt-1">
              Extraordinary
            </span>
          </h1>

          <p
            ref={subRef}
            className="mt-5 text-lg md:text-xl text-gray-500 max-w-xl mx-auto font-light leading-relaxed opacity-0 translate-y-6"
          >
            Book a private consultation. No sales pitch — just a focused conversation about your vision.
          </p>

          {/* Perks row */}
          <div
            ref={perksRef}
            className="mt-8 flex items-center justify-center gap-6 opacity-0 translate-y-6"
          >
            {perks.map((perk, i) => (
              <div key={i} className="flex items-center gap-2.5 text-sm text-gray-500">
                <div className="w-8 h-8 rounded-lg bg-white/[0.03] border border-white/10 flex items-center justify-center text-blue-400">
                  {perk.icon}
                </div>
                <div className="text-left hidden sm:block">
                  <div className="text-gray-300 font-medium text-xs">{perk.label}</div>
                  <div className="text-[11px] text-gray-600">{perk.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Section divider */}
        <div className="relative py-6 flex items-center justify-center">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" />
          </div>
          <div className="relative z-10 w-1.5 h-1.5 rounded-full bg-blue-500/40 shadow-[0_0_10px_rgba(59,130,246,0.4)] animate-pulse" />
        </div>

        {/* Calendly iframe — the star */}
        <section className="px-4 pb-20 flex justify-center">
          <div
            ref={iframeContainerRef}
            className="relative w-full max-w-4xl opacity-0 translate-y-10 scale-[0.98]"
          >
            {/* Glow behind */}
            <div className="absolute -inset-3 bg-gradient-to-r from-blue-600/20 via-purple-600/15 to-blue-600/20 rounded-3xl blur-2xl opacity-60" />

            {/* Glass frame */}
            <div className="relative rounded-2xl overflow-hidden bg-white/[0.02] backdrop-blur-2xl border border-white/[0.08] calendly-glow">
              {/* Top gradient line */}
              <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-blue-500/40 to-transparent z-20" />

              {/* Loading state */}
              {loading && (
                <div className="flex flex-col items-center justify-center py-32">
                  <div className="relative">
                    <div className="w-12 h-12 rounded-full border-2 border-white/5" />
                    <div className="absolute inset-0 w-12 h-12 rounded-full border-t-2 border-blue-500 animate-spin" />
                  </div>
                  <p className="mt-5 text-sm text-gray-500 font-medium tracking-wide uppercase">
                    Initializing calendar
                  </p>
                  <p className="mt-1 text-xs text-gray-700">Secure connection to Calendly</p>
                </div>
              )}

              <iframe
                src="https://calendly.com/mnrtechnologies-info/30min?hide_landing_page_details=1&background_color=0f172a&text_color=e2e8f0&primary_color=3b82f6"
                className={`w-full h-[700px] border-none ${loading ? "hidden" : "block"}`}
                title="Calendly Booking"
                onLoad={() => setLoading(false)}
              />
            </div>

            {/* Trust microcopy below */}
            <div className="mt-6 flex items-center justify-center gap-6 text-[11px] text-gray-600 uppercase tracking-widest font-medium">
              <span className="flex items-center gap-1.5">
                <div className="w-1 h-1 rounded-full bg-emerald-500" />
                SSL Encrypted
              </span>
              <span className="flex items-center gap-1.5">
                <div className="w-1 h-1 rounded-full bg-blue-500" />
                No credit card required
              </span>
              <span className="flex items-center gap-1.5">
                <div className="w-1 h-1 rounded-full bg-purple-500" />
                Cancel anytime
              </span>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}